import { IComment, ITicketTrailEvent } from '@tymely/atoms';
import { LlmTrailEvent } from '@tymely/atoms/llmTrail.atoms';
import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import isThisYear from 'date-fns/isThisYear';
import enUS from 'date-fns/locale/en-US';
import sanitizeHtml_, { defaults, Tag } from 'sanitize-html';

import { eventOperations, operationOrder } from './constants';
import { TICKET_OPERATIONS } from '../../constants';
import { Part } from './ChatEvent';

// Minimal type for conversation messages
export type Message = {
    kind?: string;
    parts?: Part[];
    timestamp?: string;
    instructions?: unknown;
    [key: string]: unknown;
};

export type DateEvent = { label: string; date: string; subtitle?: string };
export type CommentDateEvents = { dateEvents: DateEvent[] };

export const getIntentIdsFromArg = (argValue: unknown): number[] => {
    return Array.isArray(argValue)
        ? (argValue as number[])
        : argValue !== undefined && argValue !== null
          ? [argValue as number]
          : [];
};

export const formatBubbleDate = (dateStr: string, withDate?: boolean, precise?: boolean) => {
    try {
        const normalizedDateStr = dateStr.endsWith('Z') ? dateStr : `${dateStr}Z`;
        const date = new Date(normalizedDateStr);
        const offset = `UTC${date.getTimezoneOffset() <= 0 ? '+' : '-'}${Math.abs(date.getTimezoneOffset()) / 60}`;
        return `${format(date, `${withDate ? 'MMM d,' : ''} HH:mm:ss${precise ? '.SSS' : ''}`, {
            locale: enUS,
        })} ${offset}`;
    } catch (_error) {
        return null;
    }
};

export const formatGroupDate = (date: Date) => {
    try {
        if (isToday(date)) return 'Today';
        return `${format(date, `MMM d${isThisYear(date) ? '' : ', yyyy'}`, { locale: enUS })}`;
    } catch (_error) {
        return null;
    }
};

export const sanitizeHtml = (html: string) => {
    let aTag: Tag | null = null;
    return sanitizeHtml_(html, {
        ...defaults,
        allowedTags: defaults.allowedTags.filter((tag) => tag !== 'h2'),
        allowedAttributes: {
            ...defaults.allowedAttributes,
            '*': ['style', 'id', 'class'],
        },
        textFilter(text) {
            if (!aTag || !aTag.attribs['href']) {
                return text;
            }
            const isHttpLink = /^http/.test(aTag.attribs['href']);
            const href = aTag.attribs['href'].replace('mailto:', '');
            const linkHtml = `<span style="color:rgba(0, 0, 238)">${href}</span>`;
            const title = text.trim() && text !== href ? text.trim() : '';
            const link = isHttpLink ? linkHtml : href;
            aTag = null;
            return title ? `${title} (${link})` : link;
        },
        transformTags: {
            a: (tagName, attribs) => {
                if (attribs['href']?.endsWith('.mp4')) {
                    return {
                        tagName,
                        attribs,
                    };
                }

                aTag = { tagName, attribs };
                return {
                    tagName: 'span',
                    attribs: {},
                };
            },
        },
    });
};

export const isTicketTrailEvent = (item: unknown): item is ITicketTrailEvent => {
    return (item as ITicketTrailEvent).operation !== undefined;
};

export const isComment = (item: unknown): item is IComment => {
    return (item as IComment).body_html !== undefined;
};

export const getItemDate = (item: IComment | ITicketTrailEvent | LlmTrailEvent) => {
    if ('inquiry_date' in item) {
        return new Date(item.inquiry_date);
    }
    if ('created_date' in item) {
        return new Date(`${item.created_date}Z`);
    }
    return new Date();
};

export const linkRegexp = new RegExp('(https|s3)?://([^"\']*\\.(?:png|jpg|jpeg|svg|pdf|txt|mov|mp4))', 'i');

function findLastUserPromptIndex(messages: any[]): number {
    const reversedIndex = [...messages]
        .reverse()
        .findIndex((msg) => msg.parts?.some((p: any) => p.part_kind === 'user-prompt'));
    return reversedIndex === -1 ? -1 : messages.length - 1 - reversedIndex;
}

function getMessageTimestamp(message: any, fallback: string): string {
    if (message.timestamp) return message.timestamp;
    if (message.parts && Array.isArray(message.parts)) {
        const firstPartWithTimestamp = message.parts.find((p: any) => p.timestamp);
        if (firstPartWithTimestamp && firstPartWithTimestamp.timestamp) {
            return firstPartWithTimestamp.timestamp as string;
        }
    }
    return fallback;
}

const createTicketTrailEvent = (
    baseId: number,
    index: number,
    idSuffix: string,
    operation: string,
    timestamp: string,
    ticket_id: number,
    additional_data: Record<string, unknown>,
    orgId: number,
): ITicketTrailEvent => ({
    id: Number(`999${baseId}${index}${idSuffix}`),
    operation,
    created_date: new Date(timestamp) as any,
    organization_id: orgId,
    ticket_id,
    status: 'completed',
    additional_data,
});

export const getConversationEvents = (
    items: (ITicketTrailEvent | IComment | LlmTrailEvent)[],
    orgId: number,
): ITicketTrailEvent[] => {
    const events = items.reduce<ITicketTrailEvent[]>((acc, item) => {
        if ('inquiry_date' in item && item.conversation_data && Array.isArray(item.conversation_data.messages)) {
            const messages = item.conversation_data.messages.slice(0, -1) as Message[];
            const lastUserPromptIndex = findLastUserPromptIndex(messages);
            const relevantMessages = lastUserPromptIndex !== -1 ? messages.slice(lastUserPromptIndex) : messages;
            for (let index = 0; index < relevantMessages.length; index++) {
                const message = relevantMessages[index];
                const isResponse = message.kind === 'response';
                const isTrigger = message.kind === 'request';
                if (!(isResponse || isTrigger)) {
                    continue;
                }

                const finalTimestamp = getMessageTimestamp(message, item.inquiry_date);

                if (isResponse) {
                    const hasTriggerAfter = relevantMessages.slice(index + 1).some((m) => m.kind === 'request');
                    if (!hasTriggerAfter) {
                        continue;
                    }
                    const triggerMessage = relevantMessages[index + 1];
                    if (
                        triggerMessage.kind === 'request' &&
                        triggerMessage.parts?.some((p: Part) => p.part_kind === 'retry-prompt')
                    ) {
                        const additionalData = {
                            message: { timestamp: finalTimestamp },
                            responseMessage: { ...message, timestamp: finalTimestamp },
                            triggerMessage: {
                                ...triggerMessage,
                                timestamp: getMessageTimestamp(triggerMessage, item.inquiry_date),
                            },
                            audit_log: { kind: 'converser-validator', comment_id: item.id },
                        };
                        acc.push(
                            createTicketTrailEvent(
                                item.id,
                                index,
                                '',
                                eventOperations.CONVERSER_VALIDATOR,
                                finalTimestamp,
                                item.ticket_id,
                                additionalData,
                                orgId,
                            ),
                        );
                        index++;
                        continue;
                    }
                }
            }
        }
        return acc;
    }, []);

    return events.sort((a, b) => {
        const timeA = a.additional_data.message?.timestamp || a.created_date;
        const timeB = b.additional_data.message?.timestamp || b.created_date;
        return timeA.localeCompare(timeB);
    });
};

const getTimestamp = (item: ITicketTrailEvent | IComment | LlmTrailEvent): string => {
    if ('inquiry_date' in item) {
        return item.inquiry_date;
    }
    if ('created_date' in item) {
        if ('operation' in item && item.additional_data?.message?.timestamp) {
            return item.additional_data.message.timestamp;
        }
        return String(item.created_date);
    }
    return new Date().toISOString();
};

export const sortItems = (items: (ITicketTrailEvent | IComment | LlmTrailEvent)[]) => {
    return [...items].sort((a, b) => {
        const timeA = getTimestamp(a);
        const timeB = getTimestamp(b);

        const timeComparison = timeA.localeCompare(timeB);
        if (timeComparison !== 0) {
            return timeComparison;
        }

        if ('operation' in a && 'operation' in b) {
            const orderA = operationOrder[a.operation as keyof typeof operationOrder] || 6;
            const orderB = operationOrder[b.operation as keyof typeof operationOrder] || 6;
            return orderA - orderB;
        }

        return 0;
    });
};

export const getDatesEventsPerComment = (
    ticketEventsData: ITicketTrailEvent[] | null | undefined,
    items: IComment[],
): Record<number, DateEvent[]> => {
    const allTicketEvents = ticketEventsData || [];

    const eventsByCommentId = allTicketEvents.reduce<Record<number, DateEvent[]>>((acc, item) => {
        if ('operation' in item && 'additional_data' in item) {
            const event = item as ITicketTrailEvent;
            const { comment_id } = event;

            if (
                comment_id &&
                (event.operation === TICKET_OPERATIONS.START_EVALUATING_POLICY ||
                    event.operation === TICKET_OPERATIONS.END_EVALUATING_POLICY)
            ) {
                if (!acc[comment_id]) {
                    acc[comment_id] = [];
                }
                const { policy_type, policy_set_id } = event.additional_data;
                const policySetId = policy_set_id || event.additional_data?.decision?.policy_set_id;
                const type = event.operation === TICKET_OPERATIONS.START_EVALUATING_POLICY ? 'Start' : 'End';
                acc[comment_id].push({
                    label: `Policy Evaluation ${type}`,
                    date: event.created_date,
                    subtitle: policy_type === 'INTENT' ? policySetId : policy_type,
                });
            }
            if (
                event.operation === TICKET_OPERATIONS.CHAT_CONVERSER &&
                event.additional_data?.audit_log?.kind === 'response-latency' &&
                event.additional_data?.audit_log?.comment_id
            ) {
                const latencyCommentId = event.additional_data.audit_log.comment_id;
                if (!acc[latencyCommentId]) {
                    acc[latencyCommentId] = [];
                }
                acc[latencyCommentId].push({
                    label: 'Response sent',
                    date: event.created_date,
                });
            }
        }
        return acc;
    }, {});

    const finalEventsByCommentId = items.reduce<Record<number, DateEvent[]>>((acc, item) => {
        const commentId = item.id;
        if (!acc[commentId]) {
            acc[commentId] = [];
        }
        if (item.inquiry_date) {
            acc[commentId].push({
                label: 'Inquiry Date',
                date: item.inquiry_date,
            });
        }
        if (item.created_date) {
            acc[commentId].push({
                label: 'Created Date',
                date: item.created_date,
            });
        }
        return acc;
    }, eventsByCommentId);

    const getSortableDate = (date: string) => (date && !date.endsWith('Z') ? date + 'Z' : date);

    Object.values(finalEventsByCommentId).forEach((dateEvents) => {
        dateEvents.sort(
            (a, b) => new Date(getSortableDate(a.date)).getTime() - new Date(getSortableDate(b.date)).getTime(),
        );
    });

    return finalEventsByCommentId;
};

export function stripHtml(html: string): string {
    return html.replace(/<[^>]+>/g, '');
}

export function normalizeText(text: string): string {
    return text.replace(/\s+/g, ' ').trim();
}
